import { useAppContext } from '@/context/AppContext';
import { SCRIPTS, NEWS_ITEMS } from '@/data/mockData';
import { User, Bookmark, Star, ChevronRight, Settings, HelpCircle, Shield, FileText } from 'lucide-react';
import { Link } from 'wouter';

export function Profile() {
  const { savedScriptIds, bookmarkedNewsIds } = useAppContext();

  const savedScripts = SCRIPTS.filter(s => savedScriptIds.includes(s.id));

  return (
    <div className="flex-1 w-full max-w-3xl mx-auto py-6 px-4 md:px-8 space-y-8 animate-in fade-in duration-500 pb-24 md:pb-8">
      
      {/* Profile Header */}
      <div className="flex items-center gap-5 p-6 bg-card rounded-2xl border border-border/50 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-10 -mt-10 pointer-events-none" />
        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold shadow-md relative z-10">
          理
        </div>
        <div className="relative z-10">
          <h1 className="text-2xl font-bold">理财经理</h1>
          <p className="text-muted-foreground mt-1">专属智能金融助手</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card p-4 rounded-xl border border-border/50 flex flex-col items-center justify-center text-center shadow-sm">
          <span className="text-3xl font-bold text-foreground mb-1">{bookmarkedNewsIds.length}</span>
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Bookmark size={14} /> 收藏要闻</span>
        </div>
        <div className="bg-card p-4 rounded-xl border border-border/50 flex flex-col items-center justify-center text-center shadow-sm">
          <span className="text-3xl font-bold text-foreground mb-1">{savedScriptIds.length}</span>
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Star size={14} className="text-accent" /> 星标话术</span>
        </div>
        <div className="bg-card p-4 rounded-xl border border-border/50 flex flex-col items-center justify-center text-center shadow-sm">
          <span className="text-3xl font-bold text-foreground mb-1">12</span>
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><FileText size={14} /> 今日触达</span>
        </div>
      </div>

      {/* Saved Scripts Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Star size={18} className="text-accent fill-accent" />
            星标话术
          </h2>
          <Link href="/scripts" className="text-sm font-medium text-primary hover:underline">
            去话术库
          </Link>
        </div>

        <div className="bg-card rounded-xl border border-border/50 overflow-hidden shadow-sm">
          {savedScripts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
              <Star size={32} className="text-muted/50" />
              暂无星标话术，请去话术库添加
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {savedScripts.map(script => (
                <Link key={script.id} href={`/scripts`} className="flex items-center p-4 hover:bg-muted/30 transition-colors group cursor-pointer block">
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{script.category}</span>
                      <h3 className="font-medium text-sm md:text-base truncate group-hover:text-primary transition-colors">{script.title}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{script.scenario}</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* App Info & Settings */}
      <section className="bg-card rounded-xl border border-border/50 overflow-hidden shadow-sm">
        <div className="divide-y divide-border/40">
          <button className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3 font-medium text-sm">
              <Settings size={18} className="text-muted-foreground" />
              通用设置
            </div>
            <ChevronRight size={18} className="text-muted-foreground" />
          </button>
          <button className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3 font-medium text-sm">
              <HelpCircle size={18} className="text-muted-foreground" />
              帮助与反馈
            </div>
            <ChevronRight size={18} className="text-muted-foreground" />
          </button>
          <button className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3 font-medium text-sm">
              <Shield size={18} className="text-muted-foreground" />
              关于财研助手
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="text-xs">v1.0.0</span>
              <ChevronRight size={18} />
            </div>
          </button>
        </div>
      </section>

    </div>
  );
}
